import { TrendingUp, TrendingDown, Wallet, Clock } from 'lucide-react';
import type {
    FinancialStats,
    Student,
    Staff,
    Account,
    BankAccount,
    Deposit,
    Transfer,
    JournalEntry,
    Transaction,
    ChartDataPoint,
    School,
} from '../../types';

// SD Sub-Schools List
export const sdSchools: School[] = [
    {
        id: 'sd-pekanbaru',
        name: 'SD Al-Azhar Syifa Budi Pekanbaru',
        income: '250.5 Jt',
        expense: '95.2 Jt',
        balance: '155.3 Jt',
        receivable: '8.5 Jt',
        color: 'bg-primary-soft text-primary',
        icon: TrendingUp
    },
    {
        id: 'sd-kampar',
        name: 'SD Al-Azhar Syifa Budi Kampar',
        income: '200.3 Jt',
        expense: '85.2 Jt',
        balance: '115.1 Jt',
        receivable: '6.7 Jt',
        color: 'bg-primary-soft text-primary',
        icon: TrendingUp
    },
];

// SD Unit Financial Statistics
export const sdStats: FinancialStats[] = [
    {
        label: 'Pendapatan Unit',
        value: 'Rp 450.800.000',
        change: '+10.5%',
        icon: TrendingUp,
    },
    {
        label: 'Pengeluaran Unit',
        value: 'Rp 180.400.000',
        change: '+4.8%',
        icon: TrendingDown,
    },
    {
        label: 'Saldo Kas Unit',
        value: 'Rp 270.400.000',
        change: '+15.2%',
        icon: Wallet,
    },
    {
        label: 'Piutang Siswa',
        value: 'Rp 15.200.000',
        change: '-3.4%',
        icon: Clock,
    },
];

// SD Students Data
export const sdStudents: Student[] = [
    {
        id: 'SD001',
        name: 'Ahmad Firdaus',
        parentName: 'Bambang Sudirjo',
        class: 'Kelas 1A',
        status: 'active',
        tuitionStatus: 'paid',
        totalBill: 'Rp 2.000.000',
        receivable: 'Rp 0',
        schoolId: 'sd-pekanbaru',
        paymentHistory: [
            { id: 'PH001', date: '2025-02-01', amount: 'Rp 1.000.000', method: 'Transfer Bank', status: 'success', receiptNo: 'INV/2025/001' },
            { id: 'PH002', date: '2025-01-01', amount: 'Rp 1.000.000', method: 'Transfer Bank', status: 'success', receiptNo: 'INV/2025/002' },
        ]
    },
    {
        id: 'SD002',
        name: 'Siti Rahma',
        parentName: 'H. Slamet',
        class: 'Kelas 2B',
        status: 'active',
        tuitionStatus: 'paid',
        totalBill: 'Rp 1.000.000',
        receivable: 'Rp 0',
        schoolId: 'sd-kampar',
        paymentHistory: [
            { id: 'PH003', date: '2025-02-05', amount: 'Rp 1.000.000', method: 'Tunai', status: 'success', receiptNo: 'INV/2025/003' },
        ]
    },
    {
        id: 'SD003',
        name: 'Budi Setiawan',
        parentName: 'Agus Santoso',
        class: 'Kelas 3A',
        status: 'active',
        tuitionStatus: 'pending',
        totalBill: 'Rp 2.400.000',
        receivable: 'Rp 1.200.000',
        amount: 'Rp 1.200.000',
        schoolId: 'sd-pekanbaru',
        paymentHistory: [
            { id: 'PH004', date: '2024-12-15', amount: 'Rp 1.200.000', method: 'Transfer Bank', status: 'success', receiptNo: 'INV/2024/099' },
        ]
    },
    {
        id: 'SD004',
        name: 'Dewi Anggraini',
        parentName: 'Indra Jaya',
        class: 'Kelas 4C',
        status: 'active',
        tuitionStatus: 'overdue',
        totalBill: 'Rp 2.400.000',
        receivable: 'Rp 2.400.000',
        amount: 'Rp 2.400.000',
        schoolId: 'sd-kampar',
        paymentHistory: []
    },
];

// SD Staff Data
export const sdStaff: Staff[] = [
    {
        id: 'STF-SD-001',
        name: 'Dr. Hendra Wijaya, M.Pd',
        position: 'Kepala Sekolah',
        department: 'Manajemen',
        status: 'active',
        schoolId: 'sd-pekanbaru',
    },
    {
        id: 'STF-SD-002',
        name: 'Ani Suryani, S.Pd',
        position: 'Guru Kelas 1',
        department: 'Pengajaran',
        status: 'active',
        schoolId: 'sd-kampar',
    },
    {
        id: 'STF-SD-003',
        name: 'Bambang Susilo, S.Pd',
        position: 'Guru Olahraga',
        department: 'Pengajaran',
        status: 'active',
        schoolId: 'sd-pekanbaru',
    },
];

// SD Chart of Accounts
export const sdAccounts: Account[] = [
    {
        id: 'ACC-SD-1000',
        code: '1-1000',
        name: 'Kas',
        type: 'asset',
        balance: 'Rp 270.400.000',
    },
    {
        id: 'ACC-SD-1100',
        code: '1-1100',
        name: 'Bank Mandiri',
        type: 'asset',
        balance: 'Rp 185.000.000',
    },
    {
        id: 'ACC-SD-1200',
        code: '1-1200',
        name: 'Piutang SPP',
        type: 'asset',
        balance: 'Rp 15.200.000',
    },
    {
        id: 'ACC-SD-4000',
        code: '4-1000',
        name: 'Pendapatan SPP',
        type: 'income',
        balance: 'Rp 385.000.000',
    },
    {
        id: 'ACC-SD-5000',
        code: '5-1000',
        name: 'Beban Gaji',
        type: 'expense',
        balance: 'Rp 125.000.000',
    },
];

// SD Bank Accounts
export const sdBankAccounts: BankAccount[] = [
    {
        id: 'BANK-SD-001',
        bankName: 'Mandiri',
        accountNumber: '1122xxxx55',
        accountName: 'SD Dasar Unit Yayasan',
        balance: 'Rp 185.000.000',
        type: 'Rekening Operasional',
        linkedAccount: '1-1100 (Bank Mandiri)',
        status: 'active',
    },
    {
        id: 'BANK-SD-002',
        bankName: 'BCA',
        accountNumber: '5544xxxx11',
        accountName: 'SD Dasar Unit - Dana BOS',
        balance: 'Rp 85.400.000',
        type: 'Rekening BOS',
        linkedAccount: '1-1110 (Bank BCA)',
        status: 'active',
    },
];

// SD Deposits (Setoran)
export const sdDeposits: Deposit[] = [
    {
        id: 'DEP-SD-001',
        date: '2025-02-01',
        type: 'masuk',
        source: 'SPP Kolektif',
        paymentMethod: 'Transfer Mandiri',
        amount: 'Rp 45.500.000',
        description: 'Setoran SPP Kolektif Februari',
        bankAccountId: 'BANK-SD-001',
        status: 'completed',
    },
    {
        id: 'DEP-SD-002',
        date: '2025-01-25',
        type: 'modal',
        source: 'Hibah Yayasan',
        paymentMethod: 'Transfer Mandiri',
        amount: 'Rp 50.000.000',
        description: 'Alokasi Dana Sarpras',
        bankAccountId: 'BANK-SD-002',
        status: 'completed',
    },
];

// SD Transfers
export const sdTransfers: Transfer[] = [
    {
        id: 'TRF-SD-001',
        date: '2025-01-20',
        fromUnit: 'SD',
        toUnit: 'SMP',
        amount: 'Rp 5.000.000',
        description: 'Subsidi Silang Kegiatan Lomba',
        status: 'completed',
    },
];

// SD Journal Entries
export const sdJournalEntries: JournalEntry[] = [
    {
        id: 'JRN-SD-001',
        date: '2025-02-01',
        description: 'Penerimaan SPP Kelas 1-6',
        debitAccount: 'Kas (1-1000)',
        creditAccount: 'Pendapatan SPP (4-1000)',
        amount: 'Rp 45.500.000',
        reference: 'SPP-SD-FEB-25',
    },
    {
        id: 'JRN-SD-002',
        date: '2025-01-31',
        description: 'Biaya Pemeliharaan Gedung',
        debitAccount: 'Beban Operasional (5-1100)',
        creditAccount: 'Kas (1-1000)',
        amount: 'Rp 12.500.000',
        reference: 'MNT-SD-JAN-25',
    },
];

// SD Transactions
export const sdTransactions: Transaction[] = [
    {
        id: 'TRX-SD-001',
        type: 'income',
        description: 'Pembayaran SPP Februari',
        amount: 'Rp 45.500.000',
        date: '2025-02-01',
        category: 'SPP',
    },
    {
        id: 'TRX-SD-002',
        type: 'expense',
        description: 'Pembelian Buku Pelajaran',
        amount: 'Rp 8.500.000',
        date: '2025-01-30',
        category: 'Operasional',
    },
];

// SD Chart Data (12 Months)
export const sdChartData: ChartDataPoint[] = [
    { month: 'Mar', income: 68000000, expense: 25000000 },
    { month: 'Apr', income: 70000000, expense: 26000000 },
    { month: 'Mei', income: 75000000, expense: 29000000 },
    { month: 'Jun', income: 65000000, expense: 35000000 },
    { month: 'Jul', income: 85000000, expense: 30000000 },
    { month: 'Agu', income: 78000000, expense: 28000000 },
    { month: 'Sep', income: 72000000, expense: 28000000 },
    { month: 'Okt', income: 75000000, expense: 30000000 },
    { month: 'Nov', income: 71000000, expense: 27500000 },
    { month: 'Des', income: 78000000, expense: 32000000 },
    { month: 'Jan', income: 74000000, expense: 29500000 },
    { month: 'Feb', income: 80800000, expense: 33400000 },
];
